import { Loader2, Search, X } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

interface SearchBarProps {
  query: string;
  onQueryChange: (query: string) => void;
  onClear: () => void;
  isSearching: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

export const SearchBar = ({
  query,
  onQueryChange,
  onClear,
  isSearching,
  placeholder = 'Search providers by name, specialty, or address…',
  autoFocus = false,
  className,
}: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleClear = useCallback(() => {
    onClear();
    inputRef.current?.focus();
  }, [onClear]);

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border-[1.5px] border-border bg-background px-5 py-3.5 shadow-sm transition-colors',
        'focus-within:border-primary/40 focus-within:ring-2 focus-within:ring-primary/20',
        className,
      )}
    >
      {/* Search icon */}
      <Search size={18} className="shrink-0 text-info-light" />

      {/* Query input */}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent text-[15px] font-medium text-info-dark placeholder:text-info-light focus:outline-none"
      />

      {/* Loading / Clear */}
      {(isSearching || query) && (
        <div className="flex shrink-0 items-center gap-1">
          {isSearching && (
            <Loader2 className="h-4 w-4 animate-spin text-info-light" />
          )}
          {query && !isSearching && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-full p-0.5 text-info-light hover:bg-background-gray-dark hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
